import { memo, FC, useEffect, useRef, useState, useCallback } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, Center, Float, useGLTF } from '@react-three/drei';
import { PostProcessing } from './post-processing';
import { EnvironmentWrapper } from './environment';
import * as THREE from 'three';
import { useControls, folder, Leva } from 'leva';
import './styles.css';

const DemoName: FC = () => (
  <div className="demo-container">
    <div className="demo-name">helmet shader</div>
    <div className="demo-author">
      made by <span className="underlined">
        <a href="https://srikarsunchu.com" target="_blank" rel="noopener noreferrer">srikar sunchu</a>
      </span>
      {" • "}
      <a href="https://github.com/srikarsunchu" target="_blank" rel="noopener noreferrer" className="github-link">GitHub</a>
    </div>
  </div>
);

// Pre-loading the model to avoid blocking the main thread later
useGLTF.preload('/Fox Supreme Helmet.glb');

/**
 * Main application component
 */
export default function App(): JSX.Element {
  const { bgColor } = useControls({
    'Scene Settings': folder({
      bgColor: {
        value: '#ffffff',
        label: 'Background Color'
      }
    })
  });

  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const [modelScale, setModelScale] = useState(3);

  const { intensity, highlight } = useControls({
    'Environment Settings': folder({
      intensity: {
        value: 0.4,
        min: 0,
        max: 5,
        step: 0.1,
        label: 'Environment Intensity'
      },
      highlight: {
        value: '#066aff',
        label: 'Highlight Color'
      }
    })
  });

  // Update renderer clear color when background color changes
  useEffect(() => {
    if (rendererRef.current) {
      rendererRef.current.setClearColor(new THREE.Color(bgColor));
    }
  }, [bgColor]);

  // Responsive adjustment handler for model scale
  const handleResize = useCallback(() => {
    const isSmallScreen = window.innerWidth <= 768;
    setModelScale(isSmallScreen ? 2.4 : 3); // 20% reduction on small screens
  }, []);

  // Set up resize handling
  useEffect(() => {
    // Initial check
    handleResize();
    
    // Add listener
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [handleResize]);

  return (
    <>
      <Leva collapsed hidden={false} />
      <Canvas 
        shadows 
        camera={{ position: [0, -1, 4], fov: 65 }}
        gl={{ 
          alpha: false 
        }}
        onCreated={({ gl }) => {
          rendererRef.current = gl;
          gl.setClearColor(new THREE.Color(bgColor));
        }}
      >
        <group position={[0, -0.5, 0]}>
          <Float 
            floatIntensity={2} 
            rotationIntensity={1}
            speed={2}
          >
            <Center scale={modelScale} position={[0, .8, 0]} rotation={[0.3, -Math.PI * 0.75, 0]}>
              <Helmet />
            </Center>
          </Float>
        </group>
        <OrbitControls />
        <EnvironmentWrapper intensity={intensity} highlight={highlight} />
        <Effects />
      </Canvas>
      <DemoName />
    </>
  )
}

/**
 * Post-processing effects wrapper component
 * Memoized to prevent unnecessary re-renders
 */
const Effects: FC = memo(() => (
  <PostProcessing />
))

/*
Model: Fox Supreme Helmet
*/
interface HelmetProps {
  [key: string]: any;
}

/**
 * 3D Helmet model component 
 */
function Helmet(props: HelmetProps): JSX.Element {
  const { nodes, materials } = useGLTF('/Fox Supreme Helmet.glb') as any;
  
  return (
    <group {...props} dispose={null}>
      <group rotation={[-Math.PI / 2, 0, 0]}>
        <mesh
          castShadow
          receiveShadow
          geometry={nodes.Object_2.geometry}
          material={materials.material}
        />
        <mesh
          castShadow
          receiveShadow
          geometry={nodes.Object_3.geometry}
          material={materials.Google}
        />
        <mesh
          castShadow
          receiveShadow
          geometry={nodes.Object_4.geometry}
          material={materials.Metalicc}
        />
        <mesh
          castShadow
          receiveShadow
          geometry={nodes.Object_5.geometry}
          material={materials.White}
        />
        <mesh
          castShadow
          receiveShadow
          geometry={nodes.Object_6.geometry}
          material={materials.bLACK}
        />
        <mesh
          castShadow
          receiveShadow
          geometry={nodes.Object_7.geometry}
          material={materials.black_gloss}
        />
        <mesh
          castShadow
          receiveShadow
          geometry={nodes.Object_8.geometry}
          material={materials.material_6}
        />
      </group>
    </group>
  )
} 